import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconSearch } from '@tabler/icons-react'
import { useDebounce } from '@/hooks/useDebounce'
import { scrollTopForRow } from '../discovery/discoveryTypes'
import { FacilityMap } from '../discovery/FacilityMap'
import { getErrorMessage } from '../shared/errorUtils'
import { usePatientGeocode } from '../shared/usePatientGeocode'
import { AddFaxForm } from './AddFaxModal'
import { ManualRecipientForm } from './ManualRecipientForm'
import { RecipientCard } from './RecipientCard'
import {
  FAX_SOURCE_LABEL,
  recipientFormData,
  recipientName,
  rememberKey,
  toSelectedRecipient,
  type FrequentRecipient,
  type ReferralSpecialtyCode,
  type SelectedRecipient,
} from './referralTypes'
import { ClinicRow, DoctorRow, FrequentRow } from './SpecialistRows'
import {
  useBookmarkRecipient,
  useReferralDoctors,
  useReferralFrequent,
  useReferralNearby,
  useRememberRecipient,
  useResolveRecipient,
  useRosterPolling,
} from './useReferralDiscovery'

type Tab = 'doctor' | 'clinic' | 'frequent'

/** A copy of `set` with `key` added or removed — state sets are never mutated in place. */
function withKey(set: Set<string>, key: string, present: boolean): Set<string> {
  const next = new Set(set)
  if (present) next.add(key)
  else next.delete(key)
  return next
}

type Props = {
  patient: Patient
  specialtyCode: ReferralSpecialtyCode | null
  specialtyLabel: string
  selected: SelectedRecipient | null
  onChange: (recipient: SelectedRecipient | null) => void
}

export const SpecialistPicker = ({
  patient,
  specialtyCode,
  specialtyLabel,
  selected,
  onChange,
}: Props) => {
  // Clinics first: it is the only tab that returns results from a specialty alone.
  const [tab, setTab] = useState<Tab>('clinic')
  const [clinicQuery, setClinicQuery] = useState('')
  const [doctorName, setDoctorName] = useState('')
  const [inStateOnly, setInStateOnly] = useState(true)
  const [scope, setScope] = useState<25 | 50 | 'state'>(25)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [resolvingKey, setResolvingKey] = useState<string | null>(null)
  // Keyed per row, so one row finishing never clears another row's spinner.
  const [rememberingKeys, setRememberingKeys] = useState<Set<string>>(() => new Set())
  // `<specialty>|<key>` of rows added this session: they count as remembered before the
  // Frequently used refetch lands, so the same provider cannot be added twice.
  const [addedKeys, setAddedKeys] = useState<Set<string>>(() => new Set())
  const [bookmarkingKeys, setBookmarkingKeys] = useState<Set<string>>(() => new Set())
  const debouncedClinic = useDebounce(clinicQuery, 400)
  const debouncedName = useDebounce(doctorName, 400)
  const {
    coords,
    patientState,
    loading: geoLoading,
  } = usePatientGeocode(patient, { zipOnly: true })
  const resolve = useResolveRecipient()
  const rememberRecipient = useRememberRecipient()
  const bookmarkRecipient = useBookmarkRecipient()

  // State-wide search has no radius to bound it, so the server needs a clinic name.
  const stateWide = scope === 'state' && Boolean(patientState)
  const clinic = debouncedClinic.trim()
  const nearby = useReferralNearby(
    tab === 'clinic' && specialtyCode && coords && (!stateWide || clinic.length >= 2)
      ? {
          lat: coords.lat,
          lon: coords.lon,
          radiusMiles: scope === 50 ? 50 : 25,
          specialtyCode,
          query: clinic || undefined,
          ...(stateWide && patientState ? { scope: 'state' as const, state: patientState } : {}),
        }
      : null,
  )
  const locations = useMemo(() => nearby.data?.locations ?? [], [nearby.data])
  const rosterUpdates = useRosterPolling(locations, specialtyCode)
  const name = debouncedName.trim()
  const patientZip = patient.address?.find((a) => a.use !== 'old')?.postalCode
  const doctors = useReferralDoctors(
    tab === 'doctor' && specialtyCode && name.length >= 2
      ? {
          specialtyCode,
          name,
          state: inStateOnly ? (patientState ?? undefined) : undefined,
          ...(coords ? { lat: coords.lat, lon: coords.lon } : {}),
          // Ranks a long NPPES match list near the patient before the server cuts it.
          ...(patientZip ? { zip: patientZip } : {}),
        }
      : null,
  )
  // Loaded on every tab: the other tabs mark the rows that are already in it.
  // Held until the geocode settles, so opening the picker fetches it once, not twice.
  const frequent = useReferralFrequent(
    specialtyCode && !geoLoading
      ? { specialtyCode, ...(coords ? { lat: coords.lat, lon: coords.lon } : {}) }
      : null,
  )
  const rememberedKeys = useMemo(() => {
    const keys = new Set((frequent.data?.recipients ?? []).map(rememberKey))
    for (const k of addedKeys) {
      if (k.startsWith(`${specialtyCode}|`)) keys.add(k.slice(k.indexOf('|') + 1))
    }
    return keys
  }, [frequent.data, addedKeys, specialtyCode])

  // Map pin → list row. The scroll lives in a callback, not only in an effect keyed on
  // `expanded`: clicking the pin of the row that is ALREADY open leaves `expanded`
  // unchanged, so an effect would never re-run and the pin would do nothing (PD-1637).
  const listRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef(new Map<string, HTMLDivElement>())
  const expandedRef = useRef(expanded)
  expandedRef.current = expanded
  const scrollRowIntoView = useCallback((placeId: string) => {
    const list = listRef.current
    const row = rowRefs.current.get(placeId)
    if (!list || !row) return
    list.scrollTop = scrollTopForRow(
      list.scrollTop,
      list.clientHeight,
      row.offsetTop,
      row.offsetHeight,
    )
  }, [])
  useEffect(() => {
    if (expanded) scrollRowIntoView(expanded)
  }, [expanded, scrollRowIntoView])
  const selectFromMap = (placeId: string) => {
    if (expandedRef.current === placeId) scrollRowIntoView(placeId)
    setExpanded(placeId)
  }

  // Only the latest selection, for the specialty still selected, may set the recipient: a
  // resolve that settles after a later click, a direct pick (manual entry, a manual Frequently
  // used row, clearing the card) or a specialty change must not replace it — nor toast or
  // clear the newer row's spinner.
  const resolveSeqRef = useRef(0)
  const specialtyRef = useRef(specialtyCode)
  specialtyRef.current = specialtyCode
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  /** Every direct selection goes through here so it supersedes any pending resolve. */
  const pick = (recipient: SelectedRecipient | null) => {
    resolveSeqRef.current++
    setResolvingKey(null)
    onChange(recipient)
  }

  const choose = async (key: string, input: { npi?: string; placeId?: string }) => {
    if (!specialtyCode) return
    const seq = ++resolveSeqRef.current
    setResolvingKey(key)
    try {
      const resolved = await resolve.mutateAsync({ specialtyCode, ...input })
      if (seq !== resolveSeqRef.current || specialtyRef.current !== specialtyCode) return
      onChange(toSelectedRecipient(resolved))
    } catch (err) {
      if (seq !== resolveSeqRef.current) return
      notifications.show({
        title: 'Could not load this provider',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      if (seq === resolveSeqRef.current) setResolvingKey(null)
    }
  }

  const chooseFrequent = (r: FrequentRecipient) => {
    if (r.npi || r.placeId) {
      void choose(r.key, { npi: r.npi, placeId: r.placeId })
      return
    }
    // A manually entered recipient: nothing to re-resolve, the saved fax is the one used before.
    pick({
      firstName: r.firstName,
      lastName: r.lastName,
      credential: r.credential,
      practiceName: r.practiceName ?? (r.lastName ? undefined : r.name),
      specialtyLabel,
      street: r.street,
      city: r.city,
      state: r.state,
      zip: r.zip,
      phone: r.phone,
      fax: r.fax,
      faxCandidates: [{ fax: r.fax, source: 'saved' }],
      manual: true,
    })
  }

  /** Resolves with the formatted fax, or `undefined` if the clinician cancels. */
  const askFax = (recipient: string, initialFax?: string, sourceLabel?: string) =>
    new Promise<string | undefined>((done) => {
      const id = modals.open({
        title: initialFax ? 'Confirm the fax number' : 'Add a fax number',
        onClose: () => done(undefined),
        children: (
          <AddFaxForm
            recipient={recipient}
            initialFax={initialFax}
            sourceLabel={sourceLabel}
            onSave={(fax) => {
              done(fax)
              modals.close(id)
            }}
            onCancel={() => modals.close(id)}
          />
        ),
      })
    })

  // Adding to Frequently used never selects the recipient, so it stays out of the resolve
  // sequence above and keeps its own spinner. A specialty change or unmount while it waits
  // drops it silently: no fax dialog, nothing saved under the old specialty.
  const remember = async (input: { npi?: string; placeId?: string }) => {
    const key = rememberKey(input)
    if (!specialtyCode || rememberingKeys.has(key)) return
    const stale = () => !mountedRef.current || specialtyRef.current !== specialtyCode
    setRememberingKeys((prev) => withKey(prev, key, true))
    try {
      const resolved = await resolve.mutateAsync({ specialtyCode, ...input })
      if (stale()) return
      const recipient = toSelectedRecipient(resolved)
      // Always confirmed: a scraped website/NPPES fax saved unseen would later show as
      // "Used before".
      const source = resolved.faxCandidates[0]?.source
      const fax = await askFax(
        recipientName(recipient),
        resolved.recommendedFax,
        source && FAX_SOURCE_LABEL[source],
      )
      if (!fax || stale()) return
      await rememberRecipient.mutateAsync(
        recipientFormData({ ...recipient, fax }, { code: specialtyCode, label: specialtyLabel }),
      )
      setAddedKeys((prev) => withKey(prev, `${specialtyCode}|${key}`, true))
      notifications.show({ message: 'Added to frequently used', color: 'teal' })
    } catch (err) {
      notifications.show({
        title: 'Could not add to frequently used',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setRememberingKeys((prev) => withKey(prev, key, false))
    }
  }

  const bookmark = async (key: string, bookmarked: boolean) => {
    setBookmarkingKeys((prev) => withKey(prev, key, true))
    try {
      await bookmarkRecipient.mutateAsync({ key, bookmarked })
    } catch (err) {
      notifications.show({
        title: 'Could not update the bookmark',
        message: getErrorMessage(err),
        color: 'red',
      })
    } finally {
      setBookmarkingKeys((prev) => withKey(prev, key, false))
    }
  }

  const rememberProps = {
    onRemember: (input: { npi?: string; placeId?: string }) => void remember(input),
    rememberedKeys,
    rememberingKeys,
  }

  const openManual = () =>
    modals.open({
      title: 'Enter the provider manually',
      size: 'lg',
      children: (
        <ManualRecipientForm
          specialtyLabel={specialtyLabel}
          onSave={(r) => {
            modals.closeAll()
            pick(r)
          }}
          onCancel={() => modals.closeAll()}
        />
      ),
    })

  if (!specialtyCode) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        Select a specialty to find a provider.
      </Text>
    )
  }

  if (selected) {
    return (
      <RecipientCard
        recipient={selected}
        onFaxChange={(fax) => pick({ ...selected, fax })}
        onClear={() => pick(null)}
      />
    )
  }

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        data={[
          { value: 'doctor', label: 'Doctor name' },
          { value: 'clinic', label: 'Clinics' },
          { value: 'frequent', label: 'Frequently used' },
        ]}
      />

      {tab === 'doctor' && (
        <Stack gap="sm">
          <Group align="flex-end" gap="sm">
            <TextInput
              className="flex-1"
              label="Doctor name"
              placeholder="e.g. Lawrence Berger"
              leftSection={<IconSearch size={16} />}
              value={doctorName}
              onChange={(e) => setDoctorName(e.currentTarget.value)}
            />
            {patientState && (
              <Checkbox
                mb={8}
                label={`In ${patientState} only`}
                checked={inStateOnly}
                onChange={(e) => setInStateOnly(e.currentTarget.checked)}
              />
            )}
          </Group>
          {name.length < 2 ? (
            <Text size="sm" c="dimmed">
              Type at least two letters of the doctor&apos;s name.
            </Text>
          ) : doctors.isLoading ? (
            <Loader size="sm" />
          ) : doctors.isError ? (
            <Alert color="red">
              Doctor search is unavailable right now. Try again, or enter the provider manually.
            </Alert>
          ) : (doctors.data?.doctors.length ?? 0) === 0 ? (
            <Text size="sm" c="dimmed">
              No {specialtyLabel} doctors match that name
              {inStateOnly && patientState ? ` in ${patientState}` : ''}.
            </Text>
          ) : (
            <Stack gap={6}>
              {doctors.data?.doctors.map((d) => (
                <DoctorRow
                  key={d.npi}
                  doctor={d}
                  busy={resolvingKey === `npi:${d.npi}`}
                  onSelect={() => void choose(`npi:${d.npi}`, { npi: d.npi })}
                  {...rememberProps}
                />
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {tab === 'clinic' && (
        <Stack gap="sm">
          <Group align="flex-end" gap="sm">
            <TextInput
              className="flex-1"
              label={stateWide ? 'Clinic name' : 'Clinic name (optional)'}
              placeholder="Search by clinic name"
              leftSection={<IconSearch size={16} />}
              value={clinicQuery}
              onChange={(e) => setClinicQuery(e.currentTarget.value)}
            />
            <SegmentedControl
              value={String(scope)}
              onChange={(v) => setScope(v === 'state' ? 'state' : v === '50' ? 50 : 25)}
              data={[
                { value: '25', label: '25 mi' },
                { value: '50', label: '50 mi' },
                ...(patientState ? [{ value: 'state', label: `All of ${patientState}` }] : []),
              ]}
            />
          </Group>
          {!coords && !geoLoading ? (
            <Alert color="yellow">
              The patient&apos;s address could not be placed on the map, so nearby search is
              unavailable. Search by doctor name instead.
            </Alert>
          ) : stateWide && clinic.length < 2 ? (
            <Text size="sm" c="dimmed">
              Type a clinic name to search all of {patientState}.
            </Text>
          ) : geoLoading || nearby.isLoading ? (
            <Group gap="xs">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                {stateWide
                  ? `Searching ${specialtyLabel} clinics in ${patientState}…`
                  : `Finding ${specialtyLabel} clinics near the patient…`}
              </Text>
            </Group>
          ) : nearby.data?.source === 'unavailable' ? (
            <Alert color="orange">
              Live clinic search is unavailable. Search by doctor name, or enter the provider
              manually.
            </Alert>
          ) : nearby.isError ? (
            <Alert color="red">Clinic search failed. Try again, or search by doctor name.</Alert>
          ) : locations.length === 0 ? (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {stateWide
                  ? `No ${specialtyLabel} clinics match that name in ${patientState}.`
                  : `No ${specialtyLabel} clinics found within ${scope} miles.`}
              </Text>
              {scope === 25 && (
                <Button variant="subtle" size="sm" onClick={() => setScope(50)}>
                  Widen search to 50 miles
                </Button>
              )}
            </Group>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,40%)]">
              <div ref={listRef} className="max-h-[560px] overflow-y-auto pr-1">
                {locations.map((l, i) => (
                  <ClinicRow
                    key={l.placeId}
                    rootRef={(el) => {
                      if (el) rowRefs.current.set(l.placeId, el)
                      else rowRefs.current.delete(l.placeId)
                    }}
                    index={i + 1}
                    location={l}
                    roster={rosterUpdates[l.placeId] ?? l.roster}
                    specialtyLabel={specialtyLabel}
                    expanded={expanded === l.placeId}
                    onToggle={() => setExpanded(expanded === l.placeId ? null : l.placeId)}
                    resolvingKey={resolvingKey}
                    onSelectDoctor={(npi) => void choose(`npi:${npi}`, { npi, placeId: l.placeId })}
                    onSelectPractice={() =>
                      void choose(`place:${l.placeId}`, { placeId: l.placeId })
                    }
                    {...rememberProps}
                  />
                ))}
              </div>
              <div className="h-[420px] lg:h-[560px]">
                <FacilityMap
                  facilities={locations}
                  selectedPlaceId={expanded}
                  hoveredPlaceId={null}
                  onSelect={selectFromMap}
                  patient={coords}
                />
              </div>
            </div>
          )}
        </Stack>
      )}

      {tab === 'frequent' &&
        (frequent.isLoading ? (
          <Loader size="sm" />
        ) : (frequent.data?.recipients.length ?? 0) === 0 ? (
          <Text size="sm" c="dimmed">
            Your clinic has not referred to a {specialtyLabel} provider yet. Providers you refer to
            appear here.
          </Text>
        ) : (
          <Stack gap={6}>
            {frequent.data?.recipients.map((r) => (
              <FrequentRow
                key={r.key}
                recipient={r}
                busy={resolvingKey === r.key}
                onSelect={() => chooseFrequent(r)}
                onBookmark={(bookmarked) => void bookmark(r.key, bookmarked)}
                bookmarking={bookmarkingKeys.has(r.key)}
              />
            ))}
          </Stack>
        ))}

      <Group>
        <Button
          variant="subtle"
          size="sm"
          leftSection={<IconPlus size={14} />}
          onClick={openManual}
        >
          Can&apos;t find the provider? Enter manually
        </Button>
      </Group>
    </Stack>
  )
}
